import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { DownOutlined, SyncOutlined } from '@ant-design/icons'
import {
  Button,
  Dropdown,
  Menu,
  Space,
  Divider,
  message,
  Tooltip,
  Badge,
  Select,
  Typography,
} from 'antd'
import { useNavigate } from 'react-router-dom'
import {
  IconFont,
  DeleteModal,
  CreateReport,
  SvgIcon,
  Message,
  FilterFormSelect,
} from '@/components'
import {
  useGetTaskListMain,
  useGetTaskListSub,
  useGetGroupList,
  useGetSiteDropdown,
  useGetArea,
  useDeleteMainTask,
  useDeleteList,
  useStartPlanTask,
  usePausePlanTask,
  useStopTask,
  useGetSecurityDeviceList,
} from './utils'
import { useAddReport } from '@/pages/evaluation/report-task/utils'
import TaskTable from '@/components/task-table/task-table'
import { RetestModal } from './retest-modal'
import DetailDrawer from './detail-drawer'
import { CompareModal } from './compare-modal'
import { FilterDropdownProps } from 'antd/lib/table/interface'
import { formatDateTime } from '@/utils/date'
import { t } from '@/i18n'

const { Text } = Typography
export interface ESDataType {
  //  执行次数上限
  executeTimesLimit: number
  // 已执行次数
  executeTimesUsed: number
  // 首次运行时间
  firstExecuteTime: string
  // 任务分组
  groupName: string
  // 任务id
  id: string
  // 执行类型
  kind: KindProps
  // 最近结束时间
  latestExecuteTime: string
  // 任务名称
  name: string
  // 任务综合评分
  score: number
  // 状态分布
  statusList: { status: string; count: number }[]
  // 验证类型
  verificationType: string
  // 资产
  assetName: string
  // 优先级
  priority: number
  // 安全域
  bizAreaName: string
  // 设备
  devices: {
    deviceIp: string
    deviceName: string
    deviceType: string
    isOnline: boolean
  }[]
}

export interface ESSubDataType {
  //  客户端
  agent: string
  //  安全域
  areaName: string
  //  用例发送数
  resourceSendTotal: number
  //  用例总数
  resourceTotal: number
  //  创建时间
  createTime: string
  // 开始时间
  startTime: string
  //  结束时间
  endTime: string
  //  任务执行id
  id: string
  //  任务评分
  score: number
  // 执行状态
  status: string
  // 离线验证任务名称
  taskName: string
  // 是否可以删除
  isRemove: boolean
}

type KindProps = 'TaskKindImmediately' | 'TaskKindBooking' | 'TaskKindCycle'

const KindMap = {
  TaskKindImmediately: t('立即'),
  TaskKindBooking: t('预定'),
  TaskKindCycle: t('周期'),
  TaskKindHighFrequency: t('高频'),
}

const VerificationTypeMap = {
  VerificationTypeOnline: { label: t('在线验证'), color: '#67C23A' },
  VerificationTypeOffline: { label: t('离线验证'), color: '#999999' },
}

const statusMap = {
  TaskStatusFinished: { label: t('已完成'), className: 'success' },
  TaskStatusRunning: { label: t('进行中'), className: 'processing' },
  TaskStatusWaiting: { label: t('等待中'), className: 'waiting' },
  TaskStatusInterrupted: { label: t('中断'), className: 'error' },
  TaskStatusError: { label: t('错误'), className: 'error' },
  TaskStatusPausing: { label: t('停止中'), className: 'warning' },
  TaskStatusPause: { label: t('暂停'), className: 'warning' },
}

const statusResultMap = {
  TaskResultStatusFinished: { label: t('已完成'), className: 'success' },
  TaskResultStatusProcessing: { label: t('进行中'), className: 'processing' },
  TaskResultStatusWaiting: { label: t('等待中'), className: 'waiting' },
  TaskResultStatusInterrupted: { label: t('中断'), className: 'error' },
  TaskResultStatusStopping: { label: t('停止中'), className: 'warning' },
}

const TaskListMain: React.FC = () => {
  const navigate = useNavigate()
  // 获取分组列表 (筛选)
  const [getGroupList, groupList] = useGetGroupList()
  // 获取安全设备列表 (筛选)
  const [getSecurityDeviceList, securityDeviceList] = useGetSecurityDeviceList()
  // 获取任务列表
  const [getTaskList, getTaskListLoading] = useGetTaskListMain()
  // 获取任务执行列表
  const [getTaskExecuteList, getTaskExecuteListLoading] = useGetTaskListSub()
  // 删除主任务
  const [deleteMainTask, deleteMainTaskLoading] = useDeleteMainTask()
  // 删除子任务
  const [deleteSubTask, deleteSubTaskLoading] = useDeleteList()
  // 创建报表
  const [createReport, createLoading] = useAddReport()
  // 获取安全域列表
  const [areaData] = useGetArea()
  // 获取站点列表
  const [siteData] = useGetSiteDropdown()
  // 计划任务开始任务
  const [startPlanTask] = useStartPlanTask()
  // 计划任务暂停任务
  const [pausePlanTask] = usePausePlanTask()
  // 任务停止
  const [stopTask] = useStopTask()

  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 10,
    isFilter: true,
    resultName: undefined,
    groupId: undefined as string | string[] | undefined,
  })
  const [total, setTotal] = useState(0)
  const [count, setCount] = useState(1)
  const [subTotal, setSubTotal] = useState(0)
  const [dataSource, setDataSource] = useState<ESDataType[]>([])
  const [subDataSource, setSubDataSource] = useState<ESSubDataType[]>([])
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [selectedSubRowKeys, setSelectedSubRowKeys] = useState<string[]>([])

  // 任务筛选
  const [taskFormValue, setTaskFormValue] = useState<{
    field: string | number
    value: string | number | string[] | number[] | undefined
  }>({
    field: '',
    value: '',
  })
  // 任务详情抽屉
  const [view, setView] = useState<{ visible: boolean; id: string }>({ visible: false, id: '' })
  // 任务复测弹窗
  let [retestVisible, setRetestVisible] = useState({
    visible: false,
    result_id: '',
    task_id: '',
  })
  // 删除任务弹窗
  const [remove, setRemove] = useState<{ visible: boolean; id: string[]; type: string }>({
    visible: false,
    id: [],
    type: '',
  })
  // 创建报表弹窗
  const [report, setReport] = useState<{
    visible: boolean
    id: string | string[]
    name: string
    multiple?: boolean
    taskList?: { task_name: string; campaign_id: string }[]
    selectItem?: any
  }>({ visible: false, id: '', multiple: false, name: '', taskList: [], selectItem: null })

  // 任务对比弹窗
  let [compareVisible, setCompareVisible] = useState<{
    visible: boolean
    result_id: string
    site: string
  }>({ visible: false, result_id: '', site: '' })

  const [mainTaskName, setMainTaskName] = useState('')
  const [assetName, setAssetName] = useState('')

  const [tableChangeLoading, setTableChangeLoading] = useState(false)
  const [groupSelectOpen, setGroupSelectOpen] = useState(false)

  const options = [
    {
      label: t('主任务名称'),
      value: 'search',
      type: 'input' as const,
    },
    {
      label: t('子任务名称'),
      value: 'resultName',
      type: 'input' as const,
    },
    {
      label: t('资产'),
      value: 'site',
      type: 'select' as const,
      mode: 'multiple',
      options: siteData?.map(item => ({
        label: item.site,
        value: item.site,
      })),
    },
    {
      label: t('安全设备'),
      value: 'deviceIds',
      type: 'select' as const,
      mode: 'multiple',
      options: securityDeviceList.map(item => ({
        label: `${item.name || '-'}  | ${item.deviceType} | ${item.deviceIp}`,
        value: item.id,
      })),
    },
    {
      label: t('安全域'),
      value: 'areaName',
      type: 'select' as const,
      options: areaData.map(item => ({
        label: item.name,
        value: item.name,
      })),
    },
  ]

  const buildItems = ({ label }, id, name, isRemove) => {
    const items = [
      {
        key: 'copy',
        label: (
          <div className="copy-btn" onClick={() => copyData(id)}>
            <span className="text">{t('任务复制')}</span>
          </div>
        ),
      },
      {
        key: 'remove',
        label: (
          <div
            className={isRemove ? 'remove-btn' : 'remove-btn-disabled'}
            onClick={() => isRemove && removeData(id, 'main')}
          >
            <span className="text">{t('删除')}</span>
          </div>
        ),
      },
    ]

    const item = {
      key: 'download',
      label: (
        <div className="download-btn" onClick={() => downloadData(id)}>
          <IconFont type="icon-xiazai1" />
          <span className="text">{t('下载离线包')}</span>
        </div>
      ),
    }

    if (label !== t('离线验证')) return <Menu items={items} />
    items.splice(1, 0, item)

    return <Menu items={items} />
  }

  const buildSubItems = (
    taskName: string,
    id: string,
    taskId: string,
    status: string,
    isRetest: boolean,
    isReport: boolean,
    isRemove: boolean,
    isReCompare: boolean,
    site?: string,
  ) => {
    const items = [
      // 报表权限
      {
        key: 'report',
        label: (
          <div
            className={isReport ? 'report-btn' : 'report-btn-disabled'}
            onClick={() => {
              if (!isReport) return
              reportData(id, taskName)
            }}
          >
            {/* <IconFont type="icon-wodebaobiao" /> */}
            <span className="text">{t('生成报表')}</span>
          </div>
        ),

        disabled: !isReport,
      },
      // 任务对比权限
      // {
      //   key: 'reCompare',
      //   label: (
      //     <div
      //       className={isReCompare ? 'report-btn' : 'report-btn-disabled'}
      //       onClick={() => {
      //         if (!isReCompare) return
      //         setCompareVisible({ visible: true, result_id: id, site: assetName || '' })
      //       }}
      //     >
      //       {/* <IconFont type="icon-duibi" /> */}
      //       <span className="text">任务对比</span>
      //     </div>
      //   ),
      //   disabled: !isReCompare,
      // },
      // 任务复测权限
      {
        key: 'retest',
        label: (
          <div
            className={isRetest ? 'report-btn' : 'report-btn-disabled'}
            onClick={() => {
              if (!isRetest) return
              setRetestVisible({
                visible: true,
                result_id: id,
                task_id: taskId,
              })
            }}
          >
            {/* <IconFont type="icon-chukuqianceshi" /> */}
            <span className="text">{t('任务复测')}</span>
          </div>
        ),

        disabled: !isRetest,
      },
      // 删除权限
      {
        key: 'remove',
        label: (
          <div
            className={isRemove ? 'download-btn' : 'download-btn-disabled'}
            onClick={() => {
              if (!isRemove) return
              removeData(id, 'sub')
            }}
          >
            {/* <IconFont type="icon-shanchu-m" /> */}
            <span className="text">{t('删除')}</span>
          </div>
        ),

        disabled: !isRemove,
      },
    ]

    return <Menu items={items} />
  }

  const columns = [
    {
      title: t('任务名称'),
      dataIndex: 'name',
      key: 'name',
      width: '11%',
      ellipsis: true,
      render: (_, record) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title={record.name} getPopupContainer={() => document.body}>
            <span
              style={{
                maxWidth: 'calc(100% - 24px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {record.name}
            </span>
          </Tooltip>
          {record.devices?.length > 0 && (
            <Tooltip
              getPopupContainer={() => document.body}
              overlayStyle={{ maxWidth: 'min(800px, 90vw)' }}
              title={
                <div style={{ padding: 8, maxWidth: 'min(800px, 90vw)' }}>
                  <div style={{ marginBottom: 4 }}>{t('设备清单')}</div>
                  {record.devices.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: 5,
                        flexWrap: 'nowrap',
                        width: '100%',
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: '1 1 auto',
                          minWidth: 0,
                        }}
                      >
                        {item.deviceType} | {item.deviceName} | {item.deviceIp}
                      </span>
                      <Badge
                        style={{ margin: '0 8px', flexShrink: 0 }}
                        status={item.isOnline ? 'success' : 'default'}
                      />

                      <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {item.isOnline ? t('在线') : t('离线')}
                      </span>
                    </div>
                  ))}
                </div>
              }
            >
              <SvgIcon
                name={record.devices.some(d => d.isOnline) ? t('设备') : t('设备灰')}
                style={{ width: 16, height: 16, flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: t('任务综合评分'),
      dataIndex: 'score',
      key: 'score',
      width: '13%',
      render: ({
        score,
        riskLevel,
        className,
      }: {
        score: number
        riskLevel: string
        className: string
      }) => (
        <span>
          <span className="score">{typeof score === 'number' ? score / 100 : score}</span>
          <span className="divider">|</span>
          <span className={className}>{riskLevel}</span>
        </span>
      ),

      sorter: (a: ESDataType, b: ESDataType) => a.score - b.score,
    },
    {
      title: t('运行状态分布'),
      dataIndex: 'statusList',
      key: 'statusList',
      width: '18%',
      render: statusList => (
        <div style={{ display: 'flex' }}>
          {statusList?.map(status => (
            <div key={status?.name} className={`status ${status?.className}`}>
              <span>{status?.name}</span>
              <span className="count">{status?.count}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: t('资产'),
      dataIndex: 'assetName',
      key: 'assetName',
      width: '18%',
      ellipsis: true,
      render: (_, record: ESDataType) => (
        <Tooltip
          title={`${record.bizAreaName} - ${record.assetName}`}
          getPopupContainer={() => document.body}
        >
          <span>{record.assetName}</span>
        </Tooltip>
      ),
    },
    {
      title: t('执行类型'),
      dataIndex: 'kind',
      key: 'kind',
      width: '10%',
      render: (kind: string, { executeTimesUsed, executeTimesLimit }: ESDataType) => (
        <div
          className={
            [t('立即'), t('预定')].includes(kind) ? 'kind-immediately kind-wrapper' : 'kind-wrapper'
          }
        >
          <span className="kind">{kind}</span>
          {![t('立即'), t('预定')].includes(kind) && (
            <>
              <span>{executeTimesUsed || 0}</span>
              <span className="divider">|</span>
              <span>{executeTimesLimit === 0 ? '∞' : executeTimesLimit}</span>
            </>
          )}
        </div>
      ),
    },
    {
      title: t('优先级'),
      dataIndex: 'priority',
      key: 'priority',
      width: '7%',
      ellipsis: true,
      sorter: (a: ESDataType, b: ESDataType) => a.priority - b.priority,
    },
    {
      title: t('分组'),
      dataIndex: 'groupId',
      key: 'groupId',
      width: '10%',
      filteredValue: searchParams?.groupId ? searchParams?.groupId : '',
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder={t('请选择分组')}
            value={props.selectedKeys as string[]}
            open={groupSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            dropdownClassName="table-filter-dropdown"
            onDropdownVisibleChange={setGroupSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
              setSearchParams(prev => ({ ...prev, groupId: value, isFilter: true }))
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setGroupSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={groupList.map(type => ({
              label: type.groupName,
              value: type.id,
            }))}
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      render: (_, record) => <span>{record.groupName || '-'}</span>,
    },
    {
      title: t('首次运行时间'),
      dataIndex: 'firstExecuteTime',
      key: 'firstExecuteTime',
      width: '10%',
      ellipsis: true,
      render: text => (
        <Tooltip
          title={text === '-' ? '-' : formatDateTime(text, 'standard')}
          getPopupContainer={() => document.body}
        >
          {text === '-' ? '-' : formatDateTime(text, 'date')}
        </Tooltip>
      ),
    },
    {
      title: t('最近结束时间'),
      dataIndex: 'latestExecuteTime',
      key: 'latestExecuteTime',
      width: '10%',
      ellipsis: true,
      render: text => (
        <Tooltip
          title={text === '-' ? '-' : formatDateTime(text, 'standard')}
          getPopupContainer={() => document.body}
        >
          {text === '-' ? '-' : formatDateTime(text, 'date')}
        </Tooltip>
      ),
    },
    {
      title: t('操作'),
      key: 'action',
      width: '20%',
      fixed: 'right',
      render: (_, { id, verificationType, taskStatus, name, exceptReason, isRemove }) => (
        <Space size="middle">
          {taskStatus && (
            <div className="stop-btn">
              {[t('等待中'), t('中断'), t('暂停'), t('错误')].includes(taskStatus) && (
                <span
                  onClick={() => {
                    if (taskStatus === t('中断') || taskStatus === t('错误')) {
                      message.warn({
                        className: 'task-warn',
                        content: (
                          <div>
                            <div style={{ textAlign: 'left' }}>{t('任务异常，无法运行')}</div>
                            {t('异常原因：')}
                            {exceptReason}
                          </div>
                        ),
                      })
                      return
                    }
                    handleOpenTask(id, 2)
                  }}
                >
                  {/* <IconFont type="icon-zanting1" style={{ fontSize: '12px' }} /> */}
                  <span className="text">{t('开启')}</span>
                </span>
              )}
              {[t('进行中')].includes(taskStatus) && (
                <span onClick={() => handleOpenTask(id, 1)}>
                  {/* <IconFont type="icon-zanting2" style={{ fontSize: '12px' }} /> */}
                  <span className="text">{t('暂停')}</span>
                </span>
              )}
            </div>
          )}
          <div className="view-btn" onClick={() => viewData(id)}>
            {/* <IconFont type="icon-chakan" /> */}
            <span className="text">{t('查看')}</span>
          </div>
          <Dropdown
            overlay={buildItems(verificationType, id, name, isRemove)}
            getPopupContainer={() => document.body}
            overlayStyle={{ zIndex: 9999 }}
          >
            <span>
              {t('更多')} <DownOutlined />
            </span>
          </Dropdown>
        </Space>
      ),
    },
  ]

  const subColumns = useMemo(() => {
    const arr = [
      {
        title: t('子任务名称'),
        dataIndex: 'taskName',
        key: 'taskName',
        width: '20%',
        ellipsis: true,
        render: (_, record) => (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tooltip title={record.taskName} getPopupContainer={() => document.body}>
              <span
                style={{
                  maxWidth: 'calc(100% - 24px)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {record.taskName}
              </span>
            </Tooltip>
            {record.devices?.length > 0 && (
              <Tooltip
                getPopupContainer={() => document.body}
                overlayStyle={{ maxWidth: 'min(800px, 90vw)' }}
                title={
                  <div style={{ padding: 8, maxWidth: 'min(800px, 90vw)' }}>
                    <div style={{ marginBottom: 4 }}>{t('设备清单')}</div>
                    {record.devices.map((item, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginTop: 5,
                          flexWrap: 'nowrap',
                          width: '100%',
                        }}
                      >
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: '1 1 auto',
                            minWidth: 0,
                          }}
                        >
                          {item.deviceType} | {item.deviceName} | {item.deviceIp}
                        </span>
                        <Badge
                          style={{ margin: '0 8px', flexShrink: 0 }}
                          status={item.isOnline ? 'success' : 'default'}
                        />

                        <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {item.isOnline ? t('在线') : t('离线')}
                        </span>
                      </div>
                    ))}
                  </div>
                }
              >
                <SvgIcon
                  name={record.devices.some(d => d.isOnline) ? t('设备') : t('设备灰')}
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </span>
        ),
      },
      {
        title: t('任务评分'),
        dataIndex: 'score',
        key: 'score',
        width: '16%',
        render: ({
          score,
          riskLevel,
          className,
        }: {
          score: number
          riskLevel: string
          className: string
        }) => (
          <span>
            <span className="score">{typeof score === 'number' ? score / 100 : score}</span>
            <span className="divider">|</span>
            <span className={className}>{riskLevel}</span>
          </span>
        ),
      },
      {
        title: t('运行状态'),
        dataIndex: 'status',
        key: 'status',
        width: '16%',
        render: (status, { resourceSendTotal, resourceTotal, exceptReason }) => (
          <div className="status-wrapper">
            <div>
              <span>{resourceSendTotal || 0}</span>
              <span className="divider">|</span>
              <span>{resourceTotal}</span>
            </div>
            <span className={`status ${status?.className}`}>{status?.label}</span>
            {status?.label === t('中断') && (
              <Tooltip title={exceptReason} getPopupContainer={() => document.body}>
                <IconFont
                  type={'icon-jinggao_jinggao'}
                  style={{ color: '#F53F3F', fontSize: '14px' }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: t('创建时间'),
        dataIndex: 'createTime',
        key: 'createTime',
        width: '10%',
        ellipsis: true,
        render: (text: string) => (
          <Tooltip
            title={text === '-' ? '-' : formatDateTime(text, 'standard')}
            getPopupContainer={() => document.body}
          >
            {text === '-' ? '-' : formatDateTime(text, 'date')}
          </Tooltip>
        ),
      },
      {
        title: t('开始时间'),
        dataIndex: 'startTime',
        key: 'startTime',
        width: '10%',
        ellipsis: true,
        render: (startTime: string) => (
          <Tooltip
            title={startTime === '-' ? '-' : formatDateTime(startTime, 'standard')}
            getPopupContainer={() => document.body}
          >
            {startTime === '-' ? '-' : formatDateTime(startTime, 'date')}
          </Tooltip>
        ),
      },
      {
        title: t('结束时间'),
        dataIndex: 'endTime',
        key: 'endTime',
        width: '10%',
        ellipsis: true,
        render: (endTime: string) => (
          <Tooltip
            title={endTime === '-' ? '-' : formatDateTime(endTime, 'standard')}
            getPopupContainer={() => document.body}
          >
            {endTime === '-' ? '-' : formatDateTime(endTime, 'date')}
          </Tooltip>
        ),
      },
      {
        title: t('操作'),
        key: 'action',
        width: '18%',
        fixed: 'right',
        render: (
          _,
          {
            taskName,
            taskId,
            id,
            status,
            stop,
            isStop,
            isView,
            isRetest,
            isReport,
            isRemove,
            isReCompare,
            site,
          },
        ) => (
          <Space size="middle">
            {isStop && (
              <div onClick={() => handleStopTask(id, taskId)} className="stop-btn">
                <IconFont type="icon-zanting2" />
                <span style={{ marginLeft: 6 }}>{t('停止')}</span>
              </div>
            )}
            <div
              className={isView ? 'view-btn' : 'view-btn-disabled'}
              onClick={() =>
                isView &&
                window.open(
                  `/validation/network/waf?tab=task&name=detail&id=${id}&task_id=${taskId}`,
                  '_blank',
                )
              }
            >
              {/* <IconFont type="icon-chakan" /> */}
              <span className="text">{t('查看')}</span>
            </div>
            <Dropdown
              overlay={buildSubItems(
                taskName,
                id,
                taskId,
                status?.label,
                isRetest,
                isReport,
                isRemove,
                isReCompare,
                site,
              )}
              getPopupContainer={() => document.body}
              overlayStyle={{ zIndex: 9999 }}
            >
              <span>
                {t('更多')} <DownOutlined />
              </span>
            </Dropdown>
          </Space>
        ),
      },
    ]

    return arr
  }, [subDataSource])

  function viewData(id: string) {
    setView({ visible: true, id })
  }

  function closeView() {
    setView({ visible: false, id: '' })
  }

  // 打开删除弹窗
  function removeData(id: string | string[], type: string) {
    if (typeof id === 'string') {
      setRemove({ visible: true, id: [id], type })
    } else {
      setRemove({ visible: true, id, type })
    }
  }

  function copyData(id: string) {
    navigate('/validation/network/waf?tab=create', {
      state: { id },
    })
  }
  // 打开报表弹窗
  function reportData(id: string, name: string) {
    const taskList = [{ task_name: name, campaign_id: id }]
    setReport({ visible: true, id, name, taskList })
  }

  function closeReport() {
    setReport({ visible: false, id: '' })
  }

  function downloadData(id) {
    console.log('download data', id)
  }

  // 停止子任务
  async function handleStopTask(id: string, taskId: string) {
    try {
      await stopTask({ result_id: id })
      initTableData(taskId, 1, true)
      Message.success({ content: t('任务停止成功'), className: 'task-success' })
    } catch (error) {
      console.log(error)
    }
  }

  // 计划任务暂停/启动
  async function handleOpenTask(id: string, taskStatus: number) {
    try {
      await (taskStatus === 1 ? pausePlanTask({ task_id: id }) : startPlanTask({ task_id: id }))
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      taskStatus === 1
        ? Message.error({ content: t('任务暂停'), className: 'task-error' })
        : Message.info({ content: t('任务启动'), className: 'task-info' })
      loadTableData(id)
    } catch (error: any) {
      if (error.code === 400) {
        message.warn({
          className: 'task-warn',
          content: (
            <span>
              <div style={{ textAlign: 'left' }}>{t('任务异常，无法运行')}</div>
              <div>
                {t('异常原因：')}
                {error.msg}
              </div>
            </span>
          ),
        })
      }
    }
  }

  // 删除任务
  const handleDelete = async () => {
    try {
      if (remove.type === 'main') {
        await deleteMainTask({ taskIds: remove.id })
      } else if (remove.type === 'sub') {
        await deleteSubTask({ ids: remove.id })
      } else {
        await deleteMainTask({ taskIds: selectedRowKeys })
        if (selectedSubRowKeys.length > 0) {
          await deleteSubTask({ ids: selectedSubRowKeys })
        }
      }
      setRemove({ visible: false, id: [], type: '' })
      setSelectedRowKeys([])
      setSelectedSubRowKeys([])
      setSearchParams({ page: 1, limit: 10 })
    } catch (error) {
      console.log(error)
    }
  }

  // 展开事件
  const handleExpand = ({ expanded, record }) => {
    if (expanded) {
      loadTableData(record.id, record.name)
      setExpandedRowKeys([record.key])
      setCount(1)
      setMainTaskName(record.name)
      setAssetName(record.assetName)
    } else {
      setExpandedRowKeys([])
      setSubDataSource([])
      setCount(1)
    }
  }

  const confirmAddGroup = values => {
    let taskData: { task_name: string; campaign_id: string }[] = []
    if (report.multiple) {
      taskData = values.campaign_ids.reduce((acc, id) => {
        const item = report.selectItem.find(i => i.id === id)
        if (item) {
          acc.push({ task_name: item.task_name, campaign_id: item.id })
        }
        return acc
      }, [])
    } else {
      if (report.taskList) {
        taskData = report.taskList
      }
    }
    console.log('values', values)

    createReport({
      task_list: taskData,
      report_name: values.report_name,
      template_type: values.template_type,
      campaign_type: values.campaign_type,
      emails: '',
      assets: 0,
      include_device_logs: values.include_device_logs,
    })
      .then(() => {
        window.open('/report/task/task-report?tab=4', '_self')
      })
      .catch(_ => {})
  }

  const buildDataSource = (data, taskId, subRowKeys, isOrigin?: boolean) => {
    if (!data || data.length === 0) return []

    const arr = JSON.parse(JSON.stringify(data))
    const tableData: ESDataType[] = []

    // 仅是更新 subRowKeys
    if (isOrigin) {
      for (let i = 0; i < arr.length; i++) {
        let item = arr[i]
        if (item.id === taskId) {
          item.subRowKeys = subRowKeys
        }

        tableData.push(item)
      }

      return tableData
    }

    for (let i = 0; i < arr.length; i++) {
      let item = arr[i]
      const {
        firstExecuteTime,
        latestExecuteTime,
        id,
        kind,
        statusList,
        taskStatus,
        verificationType,
        abnormalReason,
        devices,
      } = item
      const isUnique = !statusList.some(status => status.status !== 'TaskResultStatusInterrupted')
      const score = (
        item.score === 0 ? (isUnique ? '-' : 0) : item.score === 10000 ? 10000 : item.score || 0
      ) as number
      item.key = id
      item.devices = devices
      item.kind = KindMap[kind]
      item.firstExecuteTime = firstExecuteTime ? firstExecuteTime : '-'
      item.latestExecuteTime = latestExecuteTime ? latestExecuteTime : '-'
      item.score = {
        score,
        riskLevel: isUnique
          ? '-'
          : (score <= 10000 && score >= 8000 && t('优秀')) ||
            (score < 8000 && score >= 6000 && t('良好')) ||
            (score < 6000 && score >= 4000 && t('一般')) ||
            (score < 4000 && score >= 2000 && t('较弱')) ||
            t('弱'),

        className:
          (score <= 10000 && score >= 8000 && 'low-risk') ||
          (score < 8000 && score >= 6000 && 'medium-risk') ||
          (score < 6000 && score >= 4000 && 'high-risk') ||
          (score < 4000 && score >= 2000 && 'weaker-risk') ||
          'serious-risk',
      }
      item.verificationType = {
        label: VerificationTypeMap[verificationType]?.label,
        color: VerificationTypeMap[verificationType]?.color,
      }
      item.statusList = statusList.map(status => ({
        name: statusResultMap[status?.status]?.label,
        count: status?.count,
        className: statusResultMap[status.status]?.className,
      }))
      item.isRemove = statusList.find(status =>
        [
          'TaskResultStatusWaiting',
          'TaskResultStatusProcessing',
          'TaskResultStatusStopping',
        ].includes(status.status),
      )
        ? false
        : true

      item.taskStatus =
        kind === 'TaskKindCycle' || kind === 'TaskKindHighFrequency'
          ? statusMap[taskStatus]?.label
          : undefined
      item.exceptReason = abnormalReason ? abnormalReason : undefined

      if (item.id === taskId) {
        item.subRowKeys = subRowKeys
      }

      tableData.push(item)
    }

    return tableData
  }

  const buildSubDataSource = (data, taskId) => {
    const arr = JSON.parse(JSON.stringify(data || []))
    const subTableData: ESSubDataType[] = []
    for (let i = 0; i < arr.length; i++) {
      let item = arr[i]
      const {
        id,
        taskName,
        status,
        resourceSendTotal,
        abnormalReason,
        createTime,
        startTime,
        endTime,
      } = item
      const score = (
        item.score === 0
          ? status !== 'TaskResultStatusFinished'
            ? '-'
            : 0
          : item.score === 100
          ? 100
          : item.score || 0
      ) as number
      item.key = id
      item.taskId = taskId
      item.createTime = createTime ? createTime : '-'
      item.startTime = startTime ? startTime : '-'
      item.endTime = endTime ? endTime : '-'
      item.taskName = taskName
      item.score = {
        score,
        riskLevel:
          status !== 'TaskResultStatusFinished'
            ? '-'
            : (score <= 10000 && score >= 8000 && t('优秀')) ||
              (score < 8000 && score >= 6000 && t('良好')) ||
              (score < 6000 && score >= 4000 && t('一般')) ||
              (score < 4000 && score >= 2000 && t('较弱')) ||
              t('弱'),

        className:
          (score <= 10000 && score >= 8000 && 'low-risk') ||
          (score < 8000 && score >= 6000 && 'medium-risk') ||
          (score < 6000 && score >= 4000 && 'high-risk') ||
          (score < 4000 && score >= 2000 && 'weaker-risk') ||
          'serious-risk',
      }
      item.status = statusResultMap[status]
      item.isReport = !(
        [
          'TaskResultStatusWaiting',
          'TaskResultStatusProcessing',
          'TaskResultStatusStopping',
        ].includes(status) ||
        ([
          'TaskResultStatusProcessing',
          'TaskResultStatusStopping',
          'TaskResultStatusInterrupted',
        ].includes(status) &&
          !resourceSendTotal)
      )

      item.isRetest = !(
        [
          'TaskResultStatusWaiting',
          'TaskResultStatusProcessing',
          'TaskResultStatusStopping',
        ].includes(status) ||
        (['TaskResultStatusProcessing', 'TaskResultStatusStopping'].includes(status) &&
          !resourceSendTotal)
      )

      item.isReCompare = ![
        'TaskResultStatusWaiting',
        'TaskResultStatusProcessing',
        'TaskResultStatusStopping',
        'TaskResultStatusInterrupted',
      ].includes(status)
      item.isRemove = ![
        'TaskResultStatusWaiting',
        'TaskResultStatusProcessing',
        'TaskResultStatusStopping',
      ].includes(status)
      item.isView = !(
        [
          'TaskResultStatusWaiting',
          'TaskResultStatusProcessing',
          'TaskResultStatusInterrupted',
          'TaskResultStatusStopping',
        ].includes(status) && !resourceSendTotal
      )

      item.isStop = ['TaskResultStatusWaiting', 'TaskResultStatusProcessing'].includes(status)
      item.stop = false
      item.exceptReason = abnormalReason ? abnormalReason : undefined

      subTableData.push(item)
    }

    return subTableData
  }

  const initTableData = async (taskId?: string, page?: number, onlyRefresh?: boolean) => {
    try {
      const res = await getTaskList(searchParams)
      setTotal(res.data.total)
      if (res.data.total === 0) return setDataSource([])
      if (!mainTaskName) {
        setMainTaskName(res.data.list?.[0]?.name)
      }
      if (!assetName) {
        setAssetName(res.data.list?.[0]?.assetName)
      }
      const resp = await getTaskExecuteList(taskId || res.data.list?.[0]?.id, {
        ...searchParams,
        search: searchParams.resultName ? searchParams.resultName : undefined,
        page: onlyRefresh ? 1 : page || count,
      })
      setSubTotal(resp.data.total)
      setSubDataSource(buildSubDataSource(resp.data.list, taskId || res.data.list?.[0]?.id))
      const subRowKeys = buildSubDataSource(resp.data.list, taskId || res.data.list?.[0]?.id)
        .filter(item => item.isRemove)
        .map(v => v.id)
      setDataSource(buildDataSource(res.data.list, taskId || res.data.list?.[0]?.id, subRowKeys))

      if (onlyRefresh) return
      if (resp.data.list?.length > 0) {
        setExpandedRowKeys([taskId || res.data.list?.[0]?.id])
      } else {
        setExpandedRowKeys([])
      }
    } catch (error) {
      console.log(error)
    }
  }

  const loadTableData = async (id: string) => {
    try {
      const res = await getTaskList(searchParams)
      setTotal(res.data.total)
      let resp
      if (count !== 1) {
        resp = await getTaskExecuteList(id, {
          ...searchParams,
          page: 1,
          search: searchParams.resultName ? searchParams.resultName : undefined,
        })
      } else {
        resp = await getTaskExecuteList(id, {
          ...searchParams,
          page: count,
          search: searchParams.resultName ? searchParams.resultName : undefined,
        })
      }
      setSubTotal(resp.data.total)
      setSubDataSource(buildSubDataSource(resp.data.list, id))
      const subRowKeys = buildSubDataSource(resp.data.list, id)
        .filter(item => item.isRemove)
        .map(v => v.id)
      setDataSource(buildDataSource(res.data.list, id, subRowKeys))
      setExpandedRowKeys([id])
    } catch (error) {
      console.log(error)
    }
  }

  const loadMoreSubData = async (id: string) => {
    setCount(origin => origin + 1)
    const resp = await getTaskExecuteList(id, {
      ...searchParams,
      page: count + 1,
      search: searchParams.resultName ? searchParams.resultName : undefined,
    })
    setSubDataSource(origin => [...origin, ...buildSubDataSource(resp.data.list, id)])
    const subRowKeys = buildSubDataSource(resp.data.list, id)
      .filter(item => item.isRemove)
      .map(v => v.id)
    setDataSource(origin => [...buildDataSource(origin, id, subRowKeys, true)])
  }

  useEffect(() => {
    if (taskFormValue?.field) {
      const selectedOption = options.find(opt => opt.value === taskFormValue.field)
      if (selectedOption) {
        setSearchParams(prev => {
          if (prev[taskFormValue.field] === taskFormValue.value) {
            return prev
          }

          // 如果值为空字符串或空数组，说明是切换了字段，需要清除所有旧的筛选参数
          if (
            taskFormValue.value === '' ||
            (Array.isArray(taskFormValue.value) && taskFormValue.value.length === 0)
          ) {
            // 只保留分页相关参数
            return {
              page: 1,
              limit: prev.limit,
            }
          }
          return {
            ...prev,
            page: 1,
            isFilter: true,
            [taskFormValue.field]: taskFormValue.value,
          }
        })
      }
    }
  }, [taskFormValue])

  useEffect(() => {
    setCount(1)
    initTableData(undefined, 1)

    return () => {
      setTotal(0)
      setDataSource([])
      setSubDataSource([])
    }
  }, [searchParams])

  useEffect(() => {
    getGroupList({ page: 1, limit: 10000 }).catch(_ => {})
    getSecurityDeviceList().catch(_ => {})
  }, [])

  const batchOperation = (
    <Space split={<Divider type={'vertical'} />}>
      <div>{t('批量操作')}</div>
      <div className="batch-delete-btn" onClick={() => removeData([], 'all')}>
        <SvgIcon name={'batch_remove'} className="icon-delete" />
        <span>{t('批量删除')}</span>
      </div>
      {selectedRowKeys.length > 1 ? (
        <></>
      ) : (
        selectedSubRowKeys.length > 0 && (
          <div
            className={
              selectedSubRowKeys.length > 5 ? 'batch-report-btn disabled' : 'batch-report-btn'
            }
            onClick={() => {
              if (selectedSubRowKeys.length > 5) return
              const selectItem = [
                ...new Map(
                  subDataSource
                    .filter(item => selectedSubRowKeys.includes(item.id))
                    .map(v => [
                      v.id,
                      {
                        ...v,
                        task_name: v.taskName,
                        result_status:
                          v.status?.label === t('已完成')
                            ? 3
                            : v.status?.label === t('中断')
                            ? 4
                            : 7,
                        executed_plugin_num: v.resourceSendTotal,
                      },
                    ]),
                ).values(),
              ]

              console.log('selectItem', selectItem)

              setReport({
                visible: true,
                multiple: true,
                name: '',
                id: selectedSubRowKeys,
                selectItem,
              })
            }}
          >
            <SvgIcon
              name={selectedSubRowKeys.length > 0 ? t('批量生成报表') : 'batch_remove_ash'}
              className="icon-export"
            />

            <span>{t('批量生成报表')}</span>
          </div>
        )
      )}
    </Space>
  )

  // 处理表格变化事件
  const handleTableChange = async (pagination: any, filters: any, sorter: any) => {
    setTableChangeLoading(true)
    try {
      const params = {
        ...searchParams,
        page: pagination.current,
        limit: pagination.pageSize,
      }

      // 处理主表格筛选
      if (filters) {
        Object.keys(filters).forEach(key => {
          if (filters[key] && filters[key].length > 0) {
            params[key] = filters[key][0]
          } else {
            delete params[key]
          }
        })
      }

      // 处理主表格排序
      if (sorter && sorter.field) {
        params.sortField =
          sorter.field === 'score'
            ? 'sort_field_score'
            : sorter.field === 'firstExecuteTime'
            ? 'sort_field_update_time'
            : 'sort_field_priority'
        params.sortOrder =
          sorter.order === 'ascend'
            ? 'SORT_ORDER_ASCENDING'
            : sorter.order === 'descend'
            ? 'SORT_ORDER_DESCENDING'
            : undefined
      } else {
        delete params.sortField
        delete params.sortOrder
      }
      if (params.sortField && !params.sortOrder) {
        delete params.sortField
      }
      setSearchParams(params)
    } finally {
      setTableChangeLoading(false)
    }
  }

  // 处理子表格变化事件
  const handleSubTableChange = async (pagination: any, filters: any, sorter: any) => {}

  return (
    <Wrapper>
      {/* 标题 */}
      <div className="title-wrapper">
        <div className="title">
          <span>{t('任务列表')}</span>
        </div>
        {/* 搜素框 */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className="search-wrapper" style={{ marginRight: 8 }}>
            <FilterFormSelect
              filterOptions={options}
              value={taskFormValue}
              onChange={newValue => {
                setTaskFormValue(newValue)
              }}
            />
          </div>
          <div className="reset">
            <Button
              icon={<SyncOutlined style={{ color: '#666' }} />}
              className="reset-btn"
              onClick={() => {
                initTableData(expandedRowKeys[0], 1, true)
                setCount(1)
              }}
            />
          </div>
        </div>
      </div>
      {/* 任务列表 */}
      <div className="table-wrapper">
        <TaskTable
          batchOperation={batchOperation}
          columns={columns}
          subColumns={subColumns}
          dataSource={dataSource}
          subDataSource={subDataSource}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          total={total}
          loading={getTaskListLoading}
          hasMore={subTotal > searchParams.limit * count}
          loadMoreSubData={loadMoreSubData}
          subLoading={getTaskExecuteListLoading}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          selectedSubRowKeys={selectedSubRowKeys}
          setSelectedSubRowKeys={setSelectedSubRowKeys}
          expandedRowKeys={expandedRowKeys}
          expandHandler={handleExpand}
          onTableChange={handleTableChange}
          onSubTableChange={handleSubTableChange}
          tableChangeLoading={tableChangeLoading}
        />
      </div>
      {/* 详情抽屉 */}
      <DetailDrawer open={view.visible} onClose={closeView} id={view.id} />
      {/* 任务复测弹窗 */}
      <RetestModal
        visible={retestVisible.visible}
        onCancel={() => {
          setRetestVisible(origin => ({ ...origin, visible: false }))
        }}
        onSuccess={() => {
          setRetestVisible(origin => ({ ...origin, visible: false }))
          initTableData(retestVisible.task_id, 1)
          setCount(1)
        }}
        id={retestVisible.result_id}
        name={mainTaskName}
      />

      {/* 删除任务弹窗 */}
      <DeleteModal
        text={
          remove.type === 'main'
            ? t('是否确认删除所选任务（删除主任务时将同时删除其下的子任务）？')
            : t('此操作将永久删除，是否继续？')
        }
        open={remove.visible}
        onOk={() => handleDelete()}
        onCancel={() => setRemove(origin => ({ ...origin, visible: false, type: '' }))}
        loading={
          (remove.type === 'main' && deleteMainTaskLoading) ||
          (remove.type === 'sub' && deleteSubTaskLoading)
        }
      />

      {/* 创建报表 */}
      <CreateReport
        visible={report.visible}
        multiple={report.multiple}
        selectItem={report.selectItem}
        createLoading={createLoading}
        templateType={4}
        onConfirm={e => {
          confirmAddGroup(e)
        }}
        onCancel={closeReport}
      />

      {/* 任务对比弹窗 */}
      {compareVisible.visible && (
        <CompareModal
          setVisible={() => {
            setCompareVisible({ visible: false, result_id: '', site: '' })
          }}
          data={compareVisible}
        />
      )}
    </Wrapper>
  )
}

export default TaskListMain

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background: #ffffff;
  box-shadow: 0px 4px 8px 0px rgba(95, 103, 129, 0.06);
  border-radius: 8px;
  padding: 12px 0;
  display: flex;
  flex-direction: column;

  .title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;
    margin-bottom: 6px;

    .title {
      font-weight: 600;
      font-size: 16px;
    }

    .reset-btn {
      width: 32px;
      height: 32px;
      background: #f1f3f8;
      border-radius: 6px;
      border: none;

      &:hover {
        background: #e6e9f0;
      }
    }
  }

  .table-wrapper {
    padding: 0 20px;
    overflow-y: scroll;
  }
`

const FilterIcon = styled(IconFont)<{ $filtered?: boolean }>`
  font-size: 14px;
  color: ${props => (props.$filtered ? '#4238C9' : '#999')};
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #4238c9;
  }
`
